// Promotions (promotions plan, 22 Sep 2026) — the words and the labels, mirrored from
// `backend/promotions.py` so the editor can read an offer back as a sentence while it's
// being typed, before the server has seen it. The server's `words` wins once saved.

// What a promotion does, in plain words with an example each (Back office sweep, Oct 2026:
// D9). Free item is `kids_eat_free`: one thing free with each of another.
export const MECHANICS = [
  { value: 'rate_off', label: 'Percentage off', example: '20% off wine' },
  { value: 'amount_off', label: 'Money off', example: '£2 off a pint' },
  { value: 'set_price', label: 'Fixed price', example: 'Any bottle £25' },
  { value: 'multi_buy', label: 'Buy one get one', example: '2 for 1 cocktails' },
  { value: 'bundle', label: 'Bundle', example: 'Main and a glass £18' },
  { value: 'kids_eat_free', label: 'Free item', example: "A kids' main with each adult main" },
  { value: 'bill', label: 'Off the whole bill', example: 'Blue light 10%, a server taps it' },
];

export const KINDS = [
  { value: 'site', label: 'Site incentive' },
  { value: 'supplier', label: 'Supplier-led' },
  { value: 'competition', label: 'Staff competition' },
  { value: 'other', label: 'Other' },
];

export const STATUS_LABEL = { draft: 'Draft', live: 'Live', paused: 'Paused', ended: 'Ended' };
export const STATUS_TONE = { draft: null, live: 'ok', paused: 'warn', ended: null };

export const DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

// A section as its owner named it ("Small plates", "Beer & cider"); only an older menu's key
// ("soft_drinks") is made into words. It used to capitalise every word: "Small Plates".
export const sectionLabel = (s) => {
  const t = String(s || '');
  return /^[a-z][a-z0-9_]*$/.test(t) ? (t.charAt(0).toUpperCase() + t.slice(1)).replace(/_/g, ' ') : t;
};

const money = (v) => {
  const p = Math.round((Number(v) || 0) * 100);
  return `£${Math.floor(p / 100)}.${String(p % 100).padStart(2, '0')}`;
};

// The times an offer runs: its `windows`, or for an older one the single window its
// days/time_start/time_end describe (as `promotions.windows` on the server).
export const scheduleWindows = (sched = {}) => {
  const w = (sched.windows || []).filter(Boolean);
  return w.length ? w : [{ days: sched.days || [], time_start: sched.time_start, time_end: sched.time_end }];
};

export const scheduleWords = (sched = {}) => scheduleWindows(sched).map(windowWords).filter(Boolean).join(' and ');

const windowWords = (sched = {}) => {
  const days = (sched.days || []).map(Number);
  let dayWords = '';
  if (days.length) {
    const sorted = [...days].sort((a, b) => a - b);
    if (sorted.length === 7) dayWords = 'every day';
    else if (sorted.join() === '0,1,2,3,4') dayWords = 'weekdays';
    else if (sorted.join() === '5,6') dayWords = 'weekends';
    else if (sorted.length === 1) dayWords = `${DAYS[sorted[0]]}s`;
    else dayWords = sorted.map((d) => DAYS[d].slice(0, 3)).join('/');
  }
  const t0 = sched.time_start; const t1 = sched.time_end;
  let timeWords = '';
  if (t0 && t1) timeWords = `${t0}–${t1}`;
  else if (t0) timeWords = `from ${t0}`;
  else if (t1) timeWords = `to ${t1}`;
  return [dayWords, timeWords].filter(Boolean).join(' ');
};

const targetWords = (promo, itemNames = {}) => {
  const sel = promo.applies || promo.reward || (promo.sets || [])[0];
  if (!sel) return '';
  const names = [
    ...(sel.sections || []).map(sectionLabel),
    ...(sel.items || []).map((i) => itemNames[i] || '').filter(Boolean),
  ];
  if (!names.length) return '';
  if (names.length > 3) return `${names[0]}, ${names[1]} + ${names.length - 2} more`;
  return names.join(', ');
};

export const describe = (promo, itemNames = {}) => {
  const m = promo.mechanic;
  let what;
  if (m === 'rate_off') what = `${Number(promo.rate) || 0}% off`;
  else if (m === 'amount_off') what = `${money(promo.amount)} off`;
  else if (m === 'set_price') what = `${money(promo.price)} each`;
  else if (m === 'multi_buy') {
    const buy = Number(promo.buy) || 2; const free = Number(promo.free) || 1;
    what = buy === 1 && free === 1 ? '2-for-1' : `buy ${buy}, ${free} free`;
  } else if (m === 'bundle') what = `${money(promo.price)} for the set`;
  else if (m === 'kids_eat_free') what = 'one free per main';
  else if (m === 'bill') what = promo.rate ? `${Number(promo.rate)}% off the bill` : `${money(promo.amount)} off the bill`;
  else what = m || '';
  const promoForTarget = m === 'kids_eat_free' ? { reward: promo.reward } : m === 'bundle' ? { sets: promo.sets } : { applies: promo.applies };
  // A sentence: "One free per main", "Buy 2, 1 free".
  const parts = [what && what[0].toUpperCase() + what.slice(1), targetWords(promoForTarget, itemNames), scheduleWords(promo.schedule)].filter(Boolean);
  return parts.join(' · ');
};

// A blank offer, the way the editor starts.
export const EMPTY_PROMOTION = {
  name: '', mechanic: 'rate_off', kind: 'site', status: 'live', site_ids: [],
  schedule: { start: null, end: null, days: [], time_start: null, time_end: null, windows: [] },
  applies: { items: [], sections: [] }, rate: '', amount: '', price: '', buy: 2, free: 1,
  sets: [{ items: [], sections: [] }, { items: [], sections: [] }],
  trigger: { items: [], sections: [] }, reward: { items: [], sections: [] },
  needs_manager: false, code: '', redemption_limit: '', max_per_bill: '',
  supplier: { name: '', funding_per_unit: '' }, notes: '',
};
